import { kebabCase } from '@fergdigitalcommerce/fergy-utilities';
import { type FunctionComponent, type HTMLAttributes, type PropsWithChildren } from 'react';
import { type ProductOptionFieldsFragment } from '../../../__generated__/graphql-client-types';
import { generateDataSelector } from '../../../helpers/general-helper/general-helper';
import { type ProductResourceVariantCard } from '../../../types/product.types';
import { QuantityInput } from '../../inputs';
import { ProductImage, ProductTitle } from '../../product-components/product-information/product-information.component';
import {
	ProductDropdownVariantSelector,
	ProductVariantStock
} from '../../product-components/product-information/variant/product-variant.component';
import { ProductPrice } from '../../product-components/product-price/product-price.component';

export type ProductSelectionCardProps = {
	variant: ProductResourceVariantCard;
	quantity?: number;
	ElementTag?: keyof Pick<JSX.IntrinsicElements, 'div' | 'li' | 'section'>;
	testId?: string;
	automationHook?: string;
	variantChange?: (variantSelected: ProductResourceVariantCard) => void;
	quantityChange?: (quantity?: number) => void;
	displayModelNumber?: boolean;
} & ProductOptionFieldsFragment &
	Pick<HTMLAttributes<HTMLElement>, 'className'>;

export const ProductSelectionCard: FunctionComponent<PropsWithChildren<ProductSelectionCardProps>> = ({
	className = 'w-100 pa3',
	ElementTag = 'div',
	children,
	testId = 'selection-card',
	automationHook = 'product-selection-option',
	quantityChange,
	variantChange,
	quantity = 1,
	variant,
	displayModelNumber = false,
	...option
}) => {
	const { resource, name } = option;
	const { brandName, finishes } = resource || { finishes: [] };
	const defaultClass = `flex flex-row flex-wrap flex-nowrap-ns items-stretch justify-start br2 ba b--theme-grey-light ${className}`;
	const selectedVariant = resource?.finishes.find((finish) => finish.id === variant.id) || resource?.finishes[0];

	return resource ? (
		<ElementTag
			className={defaultClass}
			data-testid={kebabCase(`${testId}-${name}`)}
			data-automation={generateDataSelector('listitem', kebabCase(`${automationHook}-${variant.id}`))}>
			<div className="center mb2 mr3-ns">
				<ProductImage
					className="w-50 center w4-ns mb2 mr3-ns"
					image={variant.image}
					options={{ width: 128, height: 124 }}
					cloudinaryImageProps={{ useDimensions: false }}
				/>
			</div>
			<div className="flex flex-row flex-wrap items-stretch min-h-100 w-100">
				<ProductTitle className="dib mt0 w-100" brandName={brandName} title={option.name} />
				{displayModelNumber && (
					<div className={'f6 lh-title mb1'}>
						Model: <span className={'b theme-grey'}>{selectedVariant?.modelNumber}</span>
					</div>
				)}
				<ProductVariantStock
					className="mt3 w-100"
					labelClassName="f-normal"
					titleClassName="b"
					title={variant.title}
					count={variant.count || 0}
					showVariantName={finishes.length === 1}
					brand={brandName || ''}
				/>
				{variantChange && finishes.length > 1 && (
					<ProductDropdownVariantSelector
						className="w-100 mt2"
						variants={resource.finishes}
						selectedVariant={selectedVariant}
						onVariantSelected={variantChange}
					/>
				)}
				<div className="flex flex-row flex-nowrap items-center w-100 mt1">
					<span>Price: </span>
					<ProductPrice className="ml2" current={variant.price} />
				</div>
				{quantityChange && (
					<div className="mt1 w4">
						<QuantityInput quantity={quantity} setQuantity={quantityChange} automationHook="product-option-quantity" />
					</div>
				)}
				<div className="w-100">{children}</div>
			</div>
		</ElementTag>
	) : null;
};
